import React, { useState, useEffect } from 'react';
import { Smartphone, Check, ArrowLeft } from 'lucide-react';
import { getApiBaseUrl } from '../config/apiConfig';
import { useTranslation } from 'react-i18next';

function OTPVerification({ mobile, onVerificationSuccess, onBack, skipApiVerification = false }) {
  const { t } = useTranslation();
  const [otpStep, setOtpStep] = useState('otp'); // 'otp' or 'success'
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'info'
  const [timerText, setTimerText] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState('');
  const [otpExpiryTime, setOtpExpiryTime] = useState(null);

  // Initialize timer when component mounts
  useEffect(() => {
    const expiryTime = Date.now() + (5 * 60 * 1000); // 5 minutes
    setOtpExpiryTime(expiryTime);
  }, []);

  // Timer for OTP expiry
  useEffect(() => {
    if (!otpExpiryTime) return;

    const timerInterval = setInterval(() => {
      const remaining = otpExpiryTime - Date.now();

      if (remaining <= 0) {
        clearInterval(timerInterval);
        setTimerText(t('otp.expired'));
        setIsExpired(true);
        setCanResend(true);
      } else {
        const minutes = Math.floor(remaining / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);
        setTimerText(`${t('otp.expiresIn')} ${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [otpExpiryTime, t]);

  // Resend timer
  useEffect(() => {
    if (!canResend) return;

    let resendWait = 30;
    setResendTimer(`in ${resendWait}s`);

    const resendInterval = setInterval(() => {
      resendWait--;
      if (resendWait <= 0) {
        clearInterval(resendInterval);
        setResendTimer('');
      } else {
        setResendTimer(`in ${resendWait}s`);
      }
    }, 1000);

    return () => clearInterval(resendInterval);
  }, [canResend]);

  const handleOtpInput = (index, value) => {
    // Only allow numbers
    if (!/^[0-9]*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value.slice(-1); // Take only last character
    setOtpValues(newOtpValues);

    // Auto focus next input
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };

  const verifyOtp = async () => {
    const otp = otpValues.join('');

    if (otp.length !== 6) {
      showMessage(t('track.rating.pleaseSelect') || 'Please enter all 6 digits', 'error');
      return;
    }

    setIsVerifying(true);

    try {
      if (skipApiVerification) {
        setTimeout(() => {
          setOtpStep('success');
          if (onVerificationSuccess) {
            onVerificationSuccess(otp);
          }
        }, 500);
        return;
      }

      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/otp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mobile,
          otp,
        }),
      });

      const data = await response.json();

      if (response.ok && data.verified) {
        showMessage('OTP verified successfully!', 'success');
        setTimeout(() => {
          setOtpStep('success');
          if (onVerificationSuccess) {
            onVerificationSuccess(otp);
          }
        }, 1500);
      } else {
        showMessage(data.message || 'Invalid OTP', 'error');
        setIsVerifying(false);
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      showMessage('Error verifying OTP. Please try again.', 'error');
      setIsVerifying(false);
    }
  };

  const resendOtp = async () => {
    if (canResend && resendTimer) return;

    showMessage('Resending OTP...', 'info');

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/otp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber: mobile }),
      });

      if (response.ok) {
        setOtpValues(['', '', '', '', '', '']);
        setIsExpired(false);
        setCanResend(false);

        const newExpiryTime = Date.now() + (5 * 60 * 1000);
        setOtpExpiryTime(newExpiryTime);

        showMessage('OTP resent successfully!', 'success');
        document.getElementById('otp-0').focus();
      } else {
        showMessage('Failed to resend OTP', 'error');
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      showMessage('Error resending OTP. Please try again.', 'error');
    }
  };

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
  };

  const isOtpComplete = otpValues.every((digit) => digit !== '');

  if (otpStep === 'success') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black bg-opacity-50">
        <div className="w-full max-w-sm p-8 bg-white rounded-lg shadow-2xl">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full">
                <Check className="w-10 h-10 text-green-600" />
              </div>
            </div>
            <h2 className="mb-2 text-2xl font-bold text-green-600">{t('otp.success.title')}</h2>
            <p className="mb-4 text-gray-600">{t('otp.success.message')}</p>
            <p className="text-sm text-gray-500">{t('otp.mobile')} <strong>{mobile}</strong></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black bg-opacity-50">
      <div className="w-full max-w-sm p-6 bg-white rounded-lg shadow-2xl">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Smartphone className="w-6 h-6" style={{ color: '#B8860B' }} />
            <h2 className="text-2xl font-bold text-gray-900">{t('otp.title')}</h2>
          </div>
          <p className="text-sm text-gray-600">
            {t('otp.sentTo')} <strong>{mobile}</strong>
          </p>
        </div>

        {message && (
          <div
            className={`mb-4 rounded-lg p-3 text-sm text-center font-medium ${messageType === 'success'
                ? 'bg-green-100 text-green-700'
                : messageType === 'error'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-blue-100 text-blue-700'
              }`}
          >
            {message}
          </div>
        )}

        <div className="mb-4">
          <div className="flex justify-between gap-2">
            {otpValues.map((value, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={value}
                onChange={(e) => handleOtpInput(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-12 text-2xl font-bold text-center border-2 border-gray-300 rounded-lg focus:border-transparent focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': '#B8860B' }}
                disabled={isExpired}
              />
            ))}
          </div>
        </div>

        <div
          className={`mb-4 text-center font-semibold ${isExpired ? 'text-red-600' : ''
            }`}
          style={{ color: isExpired ? undefined : '#B8860B' }}
        >
          {timerText}
        </div>

        <button
          onClick={verifyOtp}
          disabled={!isOtpComplete || isExpired || isVerifying}
          className="w-full px-4 py-3 font-medium text-white transition-all rounded-lg hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            background: !isOtpComplete || isExpired || isVerifying
              ? '#cccccc'
              : 'linear-gradient(135deg, #B8860B 0%, #DAA520 100%)',
          }}
        >
          {isVerifying ? t('otp.verifying') : t('otp.verify')}
        </button>

        {(isExpired || canResend) && (
          <div className="mt-4 text-sm text-center text-gray-600">
            <span>{t('otp.didNotReceive')}</span>
            <button
              onClick={resendOtp}
              disabled={resendTimer !== '' && canResend}
              className="ml-1 font-semibold text-gray-400 hover:underline disabled:text-gray-400"
              style={{
                color: resendTimer === '' ? '#B8860B' : '#cccccc',
                cursor: resendTimer === '' ? 'pointer' : 'not-allowed',
              }}
            >
              {t('otp.resend')}
            </button>
            {resendTimer && <span> {resendTimer}</span>}
          </div>
        )}

        <button
          onClick={onBack}
          className="flex items-center justify-center w-full gap-2 px-4 py-2 mt-4 font-medium transition-colors border-2 rounded-lg hover:opacity-80"
          style={{ borderColor: '#B8860B', color: '#B8860B' }}
        >
          <ArrowLeft className="w-4 h-4" />
          {t('otp.back')}
        </button>
      </div>
    </div>
  );
}

export default OTPVerification;
